/**
 * Shared helpers for the ClemtexSearch modules.
 *
 * Deliberately depends on nothing from the SuiteCommerce core: Safe Mode
 * (SC Configuration > Advanced > Extensions) refuses extensions that require
 * private core modules, and only a handful (jQuery, underscore, Backbone,
 * Utils, ...) are public.
 */
define('Clemtex.Search.Utils', ['underscore'], function (_) {
    'use strict';

    // /api/items, /api/cacheable/items, /api/personalized/items
    var ITEMS_API = /\/api\/(?:[a-z]+\/)?items(?:\?|$)/;

    // Item field holding the manufacturer's / legacy number(s), e.g. "CSD01386"
    // or "SBM1178, 01178". Must be in the `typeahead` field set (it is, since
    // 2026-09-17). Some values are descriptive text (Kennametal), which is why
    // only EXACT matches on this field ever count.
    var ALT_NUMBER_FIELD = 'custitem_old_part_number';

    function parseParams(queryString) {
        var params = {};
        _.each(String(queryString || '').split('&'), function (pair) {
            if (!pair) { return; }
            var index = pair.indexOf('=');
            var key = index < 0 ? pair : pair.slice(0, index);
            var value = index < 0 ? '' : pair.slice(index + 1);
            try { key = decodeURIComponent(key.replace(/\+/g, ' ')); } catch (e) {}
            try { value = decodeURIComponent(value.replace(/\+/g, ' ')); } catch (e) {}
            params[key] = value;
        });
        return params;
    }

    var Utils = {
        MIN_MATCH_LENGTH: 3,
        // Prefix-matching an alternate number needs a slightly longer query than
        // an exact one: these values are a grab bag across manufacturers, so a
        // 3-character prefix would promote too broadly.
        MIN_ALT_PREFIX_LENGTH: 4,
        ALT_NUMBER_FIELD: ALT_NUMBER_FIELD,

        // "CT HDCBX360-2", "ct hdcbx360-2" and "cthdcbx3602" all become "cthdcbx3602".
        normalize: function normalize(value) {
            return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        },

        // Word searches ("nozzle") never contain a digit; every Clemtex item
        // number does. Only digit-bearing queries pay for the relevance probe.
        looksLikeItemNumber: function looksLikeItemNumber(query) {
            return /\d/.test(String(query || ''));
        },

        // Raw alternate numbers of an item (plain API object or Backbone-ish
        // object with get()), split on comma / semicolon, trimmed, non-empty.
        altNumbers: function altNumbers(item) {
            var raw = item && (item.get ? item.get(ALT_NUMBER_FIELD) : item[ALT_NUMBER_FIELD]);
            if (!raw) { return []; }
            return _.filter(_.map(String(raw).split(/[,;]/), function (s) { return s.trim(); }), Boolean);
        },

        // The raw alternate number that exactly equals the normalized query, or null.
        matchingAltNumber: function matchingAltNumber(item, normalizedQuery) {
            if (!normalizedQuery) { return null; }
            return _.find(Utils.altNumbers(item), function (alt) {
                return Utils.normalize(alt) === normalizedQuery;
            }) || null;
        },

        // Is this value a real part number we are willing to PREFIX-match, rather
        // than a description? 214 of the 3,160 online values are prose (mostly
        // Kennametal: `Right Hand; Tungsten Carbide Nozzle`, `10" Length`), and
        // prefix-matching those would promote items on fragments of their text.
        // Requires a digit, no double quote, at most one space, 4+ characters —
        // which admits `CC 28087`, `T159-6XL 12`, `145 TC` and rejects the prose.
        isPartNumberLike: function isPartNumberLike(value) {
            var raw = String(value || '').trim();
            if (raw.length < 4) { return false; }
            if (raw.indexOf('"') !== -1) { return false; }
            if ((raw.match(/ /g) || []).length > 1) { return false; }
            return /\d/.test(raw);
        },

        // The raw part-number-like alternate number that STARTS WITH the
        // normalized query, or null. Exact matches also satisfy this.
        prefixAltNumber: function prefixAltNumber(item, normalizedQuery) {
            if (!normalizedQuery || normalizedQuery.length < Utils.MIN_ALT_PREFIX_LENGTH) { return null; }
            return _.find(Utils.altNumbers(item), function (alt) {
                return Utils.isPartNumberLike(alt) &&
                    Utils.normalize(alt).indexOf(normalizedQuery) === 0;
            }) || null;
        },

        // The alternate number to credit in the UI: exact match first, else a prefix.
        matchedAltNumber: function matchedAltNumber(item, normalizedQuery) {
            return Utils.matchingAltNumber(item, normalizedQuery) ||
                Utils.prefixAltNumber(item, normalizedQuery);
        },

        isItemsApi: function isItemsApi(url) {
            return ITEMS_API.test(String(url || ''));
        },

        // "https://x/api/items?a=1&b=2" -> { base: "https://x/api/items", params: {a:"1", b:"2"} }
        parseUrl: function parseUrl(url) {
            url = String(url || '');
            var index = url.indexOf('?');
            return {
                base: index < 0 ? url : url.slice(0, index),
                params: index < 0 ? {} : parseParams(url.slice(index + 1))
            };
        },

        buildUrl: function buildUrl(base, params) {
            var pairs = _.map(_.keys(params).sort(), function (key) {
                var value = params[key];
                if (value === null || value === undefined) { return null; }
                return encodeURIComponent(key) + '=' + encodeURIComponent(String(value));
            });
            pairs = _.compact(pairs);
            return pairs.length ? base + '?' + pairs.join('&') : base;
        },

        // All query parameters of a jQuery ajax request as seen in an
        // ajaxPrefilter: at that point GET data may still live in options.data
        // (string or object) rather than in options.url.
        requestParams: function requestParams(options) {
            var params = Utils.parseUrl(options && options.url).params;
            var data = options && options.data;
            if (_.isString(data)) {
                _.extend(params, parseParams(data));
            } else if (_.isObject(data)) {
                _.extend(params, data);
            }
            return params;
        }
    };

    return Utils;
});
